<template>
  <div>
    <page-header
      :breadcrumRoutes="routes"
      title="Formulario de baja de cargas individual"
    />

    <a-card class="mb-2">
      <a-row>
        <a-col>
          <a-typography-title class="text-gray-8" :level="4">
            Datos corporativos
          </a-typography-title>
        </a-col>
      </a-row>

      <a-form
        name="form"
        layout="vertical"
        ref="formRef"
        :model="formState"
        :rules="rules"
      >
        <a-row :gutter="[16, 4]" type="flex">
          <a-col :xs="24" :md="8">
            <a-form-item label="RUT titular" has-feedback name="rut">
              <input-rut v-model="formState.rut" @change="onRutChange" />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="8">
            <a-form-item label="Fecha de baja" has-feedback name="endDate">
              <a-date-picker
                :format="dateFormat"
                placeholder="DD-MM-AAAA"
                :style="{ width: '100%' }"
                v-model:value="formState.endDate"
                :disabled="loadingTree"
                data-testid="end-date"
              />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="8">
            <a-form-item label="Empresa" name="company" has-feedback>
              <select-ordered
                placeholder="Selecciona una empresa"
                v-model="formState.company"
                :options="dataCompany"
                :disabled="!employeeId || loadingTree"
                data-testid="company-select"
                @change="onCompanyChange"
              />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item label="Aseguradora" name="insurance" has-feedback>
              <select-ordered
                placeholder="Selecciona una aseguradora"
                v-model="formState.insurance"
                :options="dataInsurance"
                :disabled="!formState.company"
                data-testid="insurance-select"
                @change="onInsuranceChange"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" type="flex" class="mb-3">
          <a-col :xs="24" :md="12">
            <a-form-item label="Pólizas" name="policies" has-feedback>
              <select-ordered
                placeholder="Selecciona una o más pólizas"
                v-model="formState.policies"
                :options="dataPolicy"
                :disabled="!formState.insurance"
                mode="multiple"
                data-testid="policy-select"
                @change="onPolicyChange"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row v-if="loadingTree" class="mb-3">
          <a-col>
            <a-spin data-testid="tree-spinner" />
            <span class="ml-2">Cargando coberturas y cargas…</span>
          </a-col>
        </a-row>

        <a-typography-title
          v-if="hasFilteredTree"
          class="text-gray-8"
          :level="4"
        >
          Cargas por cobertura
        </a-typography-title>

        <a-empty
          v-if="hasFilteredTree === false && formState.policies.length > 0"
          description="No hay coberturas activas con cargas para las pólizas seleccionadas."
          data-testid="empty-coverages"
        />

        <a-alert
          v-if="insuranceBrokerWarning"
          type="error"
          show-icon
          :message="insuranceBrokerWarning"
          class="mb-3"
          data-testid="mixed-brokers-alert"
        />
        <div v-if="hasFilteredTree" data-testid="coverage-groups" class="mb-3">
          <a-card
            v-for="policyGroup in filteredCoverageGroups"
            :key="`policy-${policyGroup.policyId}`"
            class="mb-2"
            :title="`Póliza ${policyGroup.policyNumber}`"
          >
            <div
              v-for="group in policyGroup.groups"
              :key="`group-${policyGroup.policyId}-${group.groupCompanyId}`"
              class="mb-3"
            >
              <a-typography-title class="text-gray-8" :level="5">
                {{ group.groupCompanyName }}
              </a-typography-title>
              <div
                v-for="coverage in group.coverages"
                :key="`coverage-${coverage.id}`"
                class="mb-2"
                data-testid="coverage-row"
              >
                <a-typography-title class="text-gray-8" :level="5">
                  {{
                    coverage.coverage?.name ||
                    `Cobertura ${coverage.coverage?.id || ''}`
                  }}
                </a-typography-title>
                <a-checkbox-group
                  :value="selectedDependents[coverage.id] || []"
                  @change="(vals) => onDependentToggle(coverage.id, vals)"
                  data-testid="dependent-checkbox-group"
                >
                  <a-row :gutter="[16, 8]">
                    <a-col
                      v-for="dep in getActiveDependents(coverage)"
                      :key="`dep-${coverage.id}-${dep.id}`"
                      :flex="'none'"
                    >
                      <a-checkbox :value="dep.id">
                        {{ dep.fullName || dep.rut || `Carga ${dep.id}` }}
                        <small v-if="dep.rut" class="text-gray-6">
                          ({{ dep.rut }})
                        </small>
                      </a-checkbox>
                    </a-col>
                  </a-row>
                </a-checkbox-group>
                <a-empty
                  v-if="getActiveDependents(coverage).length === 0"
                  :description="`Sin cargas activas en la cobertura ${coverage.coverage?.name || ''}`"
                />
              </div>
            </div>
          </a-card>
        </div>

        <a-typography-title class="text-gray-8" :level="4">
          Mensaje a compañía aseguradora
        </a-typography-title>

        <a-row class="mb-2" :gutter="[16, 4]">
          <a-col :xs="24" :md="12">
            <a-form-item
              has-feedback
              label="Escribe un mensaje para la compañía aseguradora"
              name="message"
            >
              <a-textarea
                v-model:value="formState.message"
                placeholder="Escribe aquí..."
                :rows="6"
              />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-row type="flex" class="justify-content-end">
      <a-col>
        <a-button
          size="large"
          class="px-4"
          type="primary"
          @click="onSubmitClick"
          :loading="submitting"
          :disabled="!!insuranceBrokerWarning"
          data-testid="submit-button"
        >
          Enviar solicitud
        </a-button>
      </a-col>
    </a-row>

    <a-modal
      :wrapClassName="'modal-xs'"
      centered
      v-model:visible="showSuccess"
      footer=""
      :maskClosable="false"
    >
      <a-result
        centered
        status="success"
        title="Ticket creado"
        :sub-title="
          createExclusionMsg ||
          'Tu ticket de baja de cargas individual ha sido creado con éxito.'
        "
      >
        <template #extra>
          <router-link
            class="justify-content-center d-flex"
            :to="{ name: 'BajaCargasDetalle', params: { id: createdTicketId } }"
          >
            <a-button key="console" type="primary">Ver ticket</a-button>
          </router-link>
        </template>
      </a-result>
    </a-modal>

    <a-modal
      :wrapClassName="'modal-xs'"
      centered
      v-model:visible="showError"
      footer=""
      :maskClosable="false"
    >
      <a-result
        centered
        status="error"
        title="Error"
        :sub-title="
          createExclusionError ||
          'No se ha podido generar el ticket. Inténtalo nuevamente.'
        "
      >
        <template #extra>
          <a-button type="primary" @click="() => (showError = false)">
            Cerrar
          </a-button>
        </template>
      </a-result>
    </a-modal>
  </div>
</template>

<script>
import PageHeader from '@/components/page-header.vue';
import InputRut from '@/bewell-components/form-controls/input-rut.vue';
import SelectOrdered from '@/bewell-components/form-controls/select-ordered.vue';
import { useExclusionForm } from './use-exclusion-form';

const routes = [
  {
    path: '/bajas-cargas',
    breadcrumbName: 'Bajas cargas'
  },
  {
    breadcrumbName: 'Formulario individual'
  }
];

const dateFormat = 'DD-MM-YYYY';

export default {
  name: 'BajaIndividual',
  components: {
    'page-header': PageHeader,
    'input-rut': InputRut,
    'select-ordered': SelectOrdered
  },
  setup() {
    const form = useExclusionForm();
    return {
      routes,
      dateFormat,
      ...form
    };
  }
};
</script>
